import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { format, addDays, startOfDay } from 'date-fns';
import Layout from '../components/Layout';
import ScheduleToolbar from '../components/schedule/ScheduleToolbar';
import ScheduleSidebar from '../components/schedule/ScheduleSidebar';
import ScheduleBoardGrid from '../components/schedule/ScheduleBoardGrid';
import ProcessStepCard from '../components/schedule/ProcessStepCard';
import ScheduleTaskCard from '../components/schedule/ScheduleTaskCard';
import SplitTaskDialog from '../components/schedule/SplitTaskDialog';
import DailySettleDialog from '../components/schedule/DailySettleDialog';
import BatchAssignDialog from '../components/schedule/BatchAssignDialog';
import EditScheduleDialog from '../components/schedule/EditScheduleDialog';
import QuickAddScheduleDialog from '../components/schedule/QuickAddScheduleDialog';
import {
  useSchedules,
  useUnassignedSteps,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
  useSplitSchedule,
  useWithdrawSplitSchedule,
  useDailySettle,
  useDailySettlements,
  useReorderSchedules,
  useHistoricalSteps,
} from '../hooks/useProductionSchedule';
import productionService from '../services/productionService';
import scheduleService from '../services/scheduleService';
import ScheduleExportImage from '../components/schedule/ScheduleExportImage';
import { captureScheduleImage, getExportTitle } from '../utils/scheduleExport';

function ProductionScheduleBoard() {
  const [startDate, setStartDate] = useState(() => startOfDay(new Date()));
  const dates = useMemo(() => {
    const d = [];
    for (let i = 0; i < 7; i++) {
      d.push(addDays(startDate, i));
    }
    return d;
  }, [startDate]);

  const [workCenters, setWorkCenters] = useState([]);
  useEffect(() => {
    productionService.getWorkCenters({ status: 'active' }).then((res) => {
      setWorkCenters(res.items || []);
    }).catch(console.error);
  }, []);

  const endDate = useMemo(() => addDays(startDate, 6), [startDate]);
  const scheduleFilters = useMemo(() => ({
    start_date: format(startDate, 'yyyy-MM-dd'),
    end_date: format(endDate, 'yyyy-MM-dd'),
  }), [startDate, endDate]);

  const { data: schedules = [], isLoading: schedulesLoading, refetch: refetchSchedules } = useSchedules(scheduleFilters);
  const { data: unassignedOrders = [], isLoading: ordersLoading, refetch: refetchOrders } = useUnassignedSteps();
  const { data: historicalOrders = [], isLoading: historicalLoading } = useHistoricalSteps();

  // Query settlements for the current date range to show settlement status in headers
  const settlementFilters = useMemo(() => ({
    start_date: format(startDate, 'yyyy-MM-dd'),
    end_date: format(endDate, 'yyyy-MM-dd'),
    page_size: 100,
  }), [startDate, endDate]);
  const { data: settlementRecords = [] } = useDailySettlements(settlementFilters);

  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();
  const splitSchedule = useSplitSchedule();
  const withdrawSplitSchedule = useWithdrawSplitSchedule();
  const reorderSchedules = useReorderSchedules();
  const dailySettle = useDailySettle();

  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState(null);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleInitialDate, setSettleInitialDate] = useState(null);
  const [settleSingleDay, setSettleSingleDay] = useState(false);
  const [batchAssignOpen, setBatchAssignOpen] = useState(false);
  const [batchAssignOrder, setBatchAssignOrder] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteTarget, setNoteTarget] = useState(null);

  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddTarget, setQuickAddTarget] = useState(null);

  const [activeDragItem, setActiveDragItem] = useState(null);
  const exportContainerRef = React.useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const schedulesByCell = useMemo(() => {
    const map = {};
    schedules.forEach((sch) => {
      const schDate = new Date(sch.scheduled_date);
      const key = `${sch.work_center_id}_${format(schDate, 'yyyy-MM-dd')}`;
      if (!map[key]) map[key] = [];
      map[key].push(sch);
    });
    return map;
  }, [schedules]);

  // Build parent -> children lookup for split relationship visualization
  const scheduleChildrenMap = useMemo(() => {
    const map = {};
    schedules.forEach((sch) => {
      const parentId = sch.parent_schedule_id;
      if (parentId) {
        if (!map[parentId]) map[parentId] = [];
        map[parentId].push(sch);
      }
    });
    return map;
  }, [schedules]);

  const scheduleDescendantCountMap = useMemo(() => {
    const countMap = {};
    const countDescendants = (scheduleId, visited = new Set()) => {
      if (visited.has(scheduleId)) return 0; // guard against cycles
      visited.add(scheduleId);
      const children = scheduleChildrenMap[scheduleId] || [];
      let count = children.length;
      for (const child of children) {
        count += countDescendants(child._id, new Set(visited));
      }
      return count;
    };
    schedules.forEach((sch) => {
      const children = scheduleChildrenMap[sch._id];
      if (children && children.length > 0) {
        countMap[sch._id] = countDescendants(sch._id);
      }
    });
    return countMap;
  }, [schedules, scheduleChildrenMap]);

  // Keep a ref to schedulesByCell so handleDragEnd doesn't need it in deps
  const schedulesByCellRef = React.useRef(schedulesByCell);
  React.useEffect(() => {
    schedulesByCellRef.current = schedulesByCell;
  }, [schedulesByCell]);

  // Daily stats for header display: assigned / completed / scrapped / status per date
  const dailyStats = useMemo(() => {
    const stats = {};
    dates.forEach((date) => {
      const dayStr = format(date, 'yyyy-MM-dd');
      const daySchedules = schedules.filter((s) => {
        try {
          return format(new Date(s.scheduled_date), 'yyyy-MM-dd') === dayStr;
        } catch { return false; }
      });
      const totalAssigned = daySchedules.reduce((sum, s) => sum + (s.assigned_quantity || 0), 0);
      const totalCompleted = daySchedules.reduce((sum, s) => sum + (s.completed_quantity || 0), 0);
      const totalScrapped = daySchedules.reduce((sum, s) => sum + (s.scrapped_quantity || 0), 0);
      const taskCount = daySchedules.length;
      const inProgress = daySchedules.some((s) => s.status === 'in_progress');
      stats[dayStr] = { totalAssigned, totalCompleted, totalScrapped, taskCount, inProgress };
    });
    return stats;
  }, [schedules, dates]);

  // Map of settled dates
  const settledDateMap = useMemo(() => {
    const map = {};
    settlementRecords.forEach((r) => {
      if (r.date) {
        try { map[format(new Date(r.date), 'yyyy-MM-dd')] = r; } catch { /* ignore */ }
      }
    });
    return map;
  }, [settlementRecords]);

  const handleDragStart = useCallback((event) => {
    setActiveDragItem(event.active.data.current);
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    setActiveDragItem(null);
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;
    if (!activeData || !overData) return;

    if (activeData.type === 'unassigned-step' && overData.type === 'board-cell') {
      try {
        await createSchedule.mutateAsync({
          production_order_id: activeData.orderId,
          production_order_number: activeData.orderNumber,
          item_id: activeData.itemId,
          item_index: activeData.itemIndex,
          finished_product_code: activeData.productCode,
          finished_product_name: activeData.productName,
          specification: activeData.specification || '',
          item_planned_quantity: activeData.plannedQuantity,
          step_sequence: activeData.stepSequence,
          step_name: activeData.stepName,
          standard_time: activeData.standardTime,
          work_center_id: overData.workCenterId,
          work_center_name: overData.workCenterName,
          scheduled_date: format(overData.date, 'yyyy-MM-dd'),
          assigned_quantity: activeData.displayQuantity ?? (activeData.plannedQuantity - (activeData.completedQuantity || 0)),
          is_subcontracted: activeData.isSubcontracted,
        });

        // Remove split child from sidebar after successful drop
        if (activeData.isSplitChild && activeData.splitId != null) {
          const key = `${activeData.orderId}-${activeData.itemId}-${activeData.stepSequence}`;
          setSplitStepMap((prev) => {
            const existing = prev[key];
            if (!existing) return prev;
            const filtered = existing.filter((s) => s.id !== activeData.splitId);
            if (filtered.length === 0) {
              const next = { ...prev };
              delete next[key];
              return next;
            }
            return { ...prev, [key]: filtered };
          });
        }
      } catch (err) {
        console.error('Failed to create schedule:', err);
      }
      return;
    }

    if (activeData.type === 'board-task' && overData.type === 'board-task') {
      const activeSchedule = activeData.schedule;
      const overSchedule = overData.schedule;
      const activeCellKey = `${activeSchedule.work_center_id}_${format(new Date(activeSchedule.scheduled_date), 'yyyy-MM-dd')}`;
      const overCellKey = `${overSchedule.work_center_id}_${format(new Date(overSchedule.scheduled_date), 'yyyy-MM-dd')}`;

      if (activeCellKey === overCellKey) {
        // Same cell: reorder
        const cellSchedules = schedulesByCellRef.current[activeCellKey] || [];
        const oldIndex = cellSchedules.findIndex((s) => s._id === activeSchedule._id);
        const newIndex = cellSchedules.findIndex((s) => s._id === overSchedule._id);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const newOrder = arrayMove(cellSchedules, oldIndex, newIndex);
          const orderedIds = newOrder.map((s) => s._id);
          try {
            await reorderSchedules.mutateAsync(orderedIds);
          } catch (err) {
            console.error('Failed to reorder schedules:', err);
          }
        }
        return;
      }
      // Different cell: fall through to cross-cell move
    }

    if (activeData.type === 'board-task' && overData.type === 'board-cell') {
      try {
        await updateSchedule.mutateAsync({
          scheduleId: activeData.scheduleId,
          updates: {
            work_center_id: overData.workCenterId,
            scheduled_date: format(overData.date, 'yyyy-MM-dd'),
          },
        });
      } catch (err) {
        console.error('Failed to move schedule:', err);
      }
      return;
    }
  }, [createSchedule, updateSchedule, reorderSchedules]);

  const handleDeleteSchedule = useCallback(async (scheduleId) => {
    if (!window.confirm('确定要删除这个排程项吗？')) return;
    try {
      await deleteSchedule.mutateAsync(scheduleId);
    } catch (err) {
      console.error('Failed to delete schedule:', err);
    }
  }, [deleteSchedule]);

  const handleSplitSchedule = useCallback((schedule) => {
    setSplitTarget(schedule);
    setSplitDialogOpen(true);
  }, []);

  const handleConfirmSplit = useCallback(async (splitQuantity) => {
    if (!splitTarget) return;
    try {
      await splitSchedule.mutateAsync({
        scheduleId: splitTarget._id,
        splitQuantity,
        notes: '',
      });
      setSplitDialogOpen(false);
      setSplitTarget(null);
    } catch (err) {
      console.error('Failed to split schedule:', err);
    }
  }, [splitTarget, splitSchedule]);

  const handleWithdrawSplit = useCallback(async (schedule, descendantCount) => {
    const hasCascade = descendantCount > 0;
    const message = hasCascade
      ? `该拆分任务还包含 ${descendantCount} 个子拆分。撤回后会将整个拆分分支合并回上级任务，是否继续？`
      : '撤回拆分后，该任务的数量将合并回上级任务，是否继续？';
    if (!window.confirm(message)) return;
    try {
      await withdrawSplitSchedule.mutateAsync(schedule._id);
    } catch (err) {
      console.error('Failed to withdraw split schedule:', err);
      alert(err?.response?.data?.error || '撤回拆分失败');
    }
  }, [withdrawSplitSchedule]);

  const handleEditNote = useCallback((schedule) => {
    setNoteTarget(schedule);
    setNoteDialogOpen(true);
  }, []);

  const handleConfirmEdit = useCallback(async ({ notes, assigned_quantity }) => {
    if (!noteTarget) return;
    try {
      await updateSchedule.mutateAsync({
        scheduleId: noteTarget._id,
        updates: { notes, assigned_quantity },
      });
      setNoteDialogOpen(false);
      setNoteTarget(null);
    } catch (err) {
      console.error('Failed to update schedule:', err);
    }
  }, [noteTarget, updateSchedule]);

  const handleQuickAdd = useCallback((workCenter, date) => {
    setQuickAddTarget({ workCenter, date });
    setQuickAddOpen(true);
  }, []);

  const handleConfirmQuickAdd = useCallback(async (formData) => {
    if (!quickAddTarget) return;
    const { workCenter, date } = quickAddTarget;
    try {
      await createSchedule.mutateAsync({
        ...formData,
        work_center_id: workCenter._id,
        work_center_name: workCenter.name,
        scheduled_date: format(date, 'yyyy-MM-dd'),
        source: 'custom',
      });
      setQuickAddOpen(false);
      setQuickAddTarget(null);
    } catch (err) {
      console.error('Failed to create custom schedule:', err);
    }
  }, [quickAddTarget, createSchedule]);

  // ===== Sidebar step splitting =====
  // Each step can be split into multiple children. We track them as an array of split records.
  // Key: `${orderId}-${itemId}-${stepSequence}`
  // Value: Array of { splitQty, id } where id is a unique counter for this step
  const [splitStepMap, setSplitStepMap] = useState({});
  const splitStepCounterRef = React.useRef(0);

  const handleSplitStep = useCallback((stepData) => {
    const {
      orderId, itemId, stepSequence, splitQuantity,
      plannedQuantity, completedQuantity,
    } = stepData;

    const remainingQty = plannedQuantity - completedQuantity;
    if (splitQuantity >= remainingQty || splitQuantity < 1) return;

    const key = `${orderId}-${itemId}-${stepSequence}`;
    setSplitStepMap((prev) => {
      const existing = prev[key];
      const nextId = ++splitStepCounterRef.current;
      if (existing && existing.length > 0) {
        // Add another child; reduce original quantity
        const totalSplit = existing.reduce((sum, s) => sum + s.splitQty, 0);
        const originalQty = remainingQty - totalSplit - splitQuantity;
        if (originalQty < 0) return prev; // cannot split more than available
        return {
          ...prev,
          [key]: [
            ...existing,
            { splitQty: splitQuantity, id: nextId },
          ],
        };
      }
      // First split
      return {
        ...prev,
        [key]: [
          { splitQty: splitQuantity, id: nextId },
        ],
      };
    });
  }, []);

  const handleUndoSplitStep = useCallback(({ orderId, itemId, stepSequence }) => {
    const key = `${orderId}-${itemId}-${stepSequence}`;
    setSplitStepMap((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Build sidebar orders with split steps applied
  const sidebarOrders = useMemo(() => {
    if (!unassignedOrders || Object.keys(splitStepMap).length === 0) return unassignedOrders;

    return unassignedOrders.map((order) => {
      const newItems = order.items?.map((item) => {
        const newSteps = [];
        item.steps?.forEach((step) => {
          const key = `${order.production_order_id}-${item.item_id}-${step.sequence}`;
          const splits = splitStepMap[key];
          if (!splits || splits.length === 0) {
            newSteps.push(step);
            return;
          }

          const totalSplit = splits.reduce((sum, s) => sum + s.splitQty, 0);
          const originalQty = (item.planned_quantity || 0) - totalSplit;

          // Original step with reduced quantity (always show, even if 0)
          newSteps.push({
            ...step,
            _splitOriginal: true,
            _displayQty: originalQty,
          });

          // Each split child as its own card
          splits.forEach((split) => {
            newSteps.push({
              ...step,
              _splitChild: true,
              _splitId: split.id,
              _displayQty: split.splitQty,
            });
          });
        });
        return { ...item, steps: newSteps };
      });
      return { ...order, items: newItems };
    });
  }, [unassignedOrders, splitStepMap]);

  const sidebarHistoricalOrders = useMemo(() => {
    if (!historicalOrders || Object.keys(splitStepMap).length === 0) return historicalOrders;

    return historicalOrders.map((order) => {
      const newItems = order.items?.map((item) => {
        const newSteps = [];
        item.steps?.forEach((step) => {
          const key = `${order.production_order_id}-${item.item_id}-${step.sequence}`;
          const splits = splitStepMap[key];
          if (!splits || splits.length === 0) {
            newSteps.push(step);
            return;
          }

          const totalSplit = splits.reduce((sum, s) => sum + s.splitQty, 0);
          const originalQty = (item.planned_quantity || 0) - totalSplit;

          newSteps.push({
            ...step,
            _splitOriginal: true,
            _displayQty: originalQty,
          });

          splits.forEach((split) => {
            newSteps.push({
              ...step,
              _splitChild: true,
              _splitId: split.id,
              _displayQty: split.splitQty,
            });
          });
        });
        return { ...item, steps: newSteps };
      });
      return { ...order, items: newItems };
    });
  }, [historicalOrders, splitStepMap]);

  const handleDailySettle = useCallback((date) => {
    setSettleInitialDate(date || dates[0] || new Date());
    setSettleSingleDay(!!date);
    setSettleDialogOpen(true);
  }, [dates]);

  const handleExportProcess = useCallback(async (schedule) => {
    try {
      const orderId = schedule.production_order_id;
      const itemIndex = schedule.item_index ?? 0;
      const { blob, filename } = await scheduleService.exportProcessDocument(orderId, itemIndex);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export process document failed:', err);
      alert('导出工序文件失败');
    }
  }, []);

  const handleExportSchedule = useCallback(async () => {
    if (!exportContainerRef.current) return;
    try {
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(addDays(startDate, 6), 'yyyy-MM-dd');
      const filename = `生产日计划_${startStr}_${endStr}.png`;
      // Give the browser a tick to ensure fonts/styles are applied
      await new Promise((resolve) => setTimeout(resolve, 100));
      await captureScheduleImage(exportContainerRef.current, filename);
    } catch (err) {
      console.error('Export image failed:', err);
      alert('导出图片失败');
    }
  }, [startDate]);

  const queryClient = useQueryClient();

  const handleConfirmSettle = useCallback(async (date, items = []) => {
    try {
      await dailySettle.mutateAsync({
        date: format(date, 'yyyy-MM-dd'),
        items,
      });
      // Clear cache and force fresh fetch to ensure settlement changes are visible
      queryClient.removeQueries({ queryKey: ['schedules'] });
      queryClient.removeQueries({ queryKey: ['unassigned-steps'] });
      await Promise.all([refetchSchedules(), refetchOrders()]);
      setSettleDialogOpen(false);
    } catch (err) {
      console.error('Failed to settle:', err);
    }
  }, [dailySettle, refetchSchedules, refetchOrders, queryClient]);

  const handleBatchAssign = useCallback((order) => {
    setBatchAssignOrder(order);
    setBatchAssignOpen(true);
  }, []);

  const handleConfirmBatchAssign = useCallback(async (workCenterId, startDate, sameDay) => {
    if (!batchAssignOrder) return;
    const wc = workCenters.find((w) => w._id === workCenterId || w.work_center_id === workCenterId);
    const wcName = wc?.name || '';

    const items = batchAssignOrder.items || [];
    let sequenceOffset = 0;

    for (const item of items) {
      for (const step of item.steps || []) {
        if (step.is_subcontracted) continue;
        const targetDate = sameDay ? startDate : addDays(startDate, sequenceOffset);
        try {
          await createSchedule.mutateAsync({
            production_order_id: batchAssignOrder.production_order_id,
            production_order_number: batchAssignOrder.production_order_number,
            item_id: item.item_id,
            item_index: item.item_index,
            finished_product_code: item.finished_product_code,
            finished_product_name: item.finished_product_name,
            item_planned_quantity: item.planned_quantity,
            step_sequence: step.sequence,
            step_name: step.name,
            standard_time: step.standard_time,
            work_center_id: workCenterId,
            work_center_name: wcName,
            scheduled_date: format(targetDate, 'yyyy-MM-dd'),
            assigned_quantity: item.planned_quantity - (item.completed_quantity || 0),
            is_subcontracted: step.is_subcontracted,
          });
        } catch (err) {
          console.error('Failed to batch assign step:', err);
        }
        if (!sameDay) sequenceOffset++;
      }
    }
    setBatchAssignOpen(false);
    setBatchAssignOrder(null);
  }, [batchAssignOrder, workCenters, createSchedule]);

  const isLoading = schedulesLoading || ordersLoading;

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-64px)]">
        <ScheduleToolbar
          startDate={startDate}
          onDateChange={setStartDate}
          dates={dates}
          workCenters={workCenters}
          isLoading={isLoading}
          onRefresh={() => {
            refetchSchedules();
            refetchOrders();
          }}
          onDailySettle={handleDailySettle}
          onExport={handleExportSchedule}
        />

        <div className="flex flex-1 overflow-hidden">
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <ScheduleSidebar
              orders={sidebarOrders}
              isLoading={ordersLoading}
              onBatchAssign={handleBatchAssign}
              onSplitStep={handleSplitStep}
              onUndoSplitStep={handleUndoSplitStep}
              historicalOrders={sidebarHistoricalOrders}
              historicalLoading={historicalLoading}
            />

            <ScheduleBoardGrid
              dates={dates}
              workCenters={workCenters}
              schedulesByCell={schedulesByCell}
              dailyStats={dailyStats}
              settledDateMap={settledDateMap}
              scheduleChildrenMap={scheduleChildrenMap}
              scheduleDescendantCountMap={scheduleDescendantCountMap}
              onDeleteSchedule={handleDeleteSchedule}
              onSplitSchedule={handleSplitSchedule}
              onWithdrawSplit={handleWithdrawSplit}
              onExportProcess={handleExportProcess}
              onDailySettle={handleDailySettle}
              onEditNote={handleEditNote}
              onQuickAdd={handleQuickAdd}
            />

            <DragOverlay>
              {activeDragItem?.type === 'unassigned-step' && (
                <ProcessStepCard
                  step={activeDragItem.step}
                  productName={activeDragItem.productName}
                  plannedQuantity={activeDragItem.plannedQuantity}
                  isOverlay
                />
              )}
              {activeDragItem?.type === 'board-task' && (
                <ScheduleTaskCard
                  schedule={activeDragItem.schedule}
                  isOverlay
                />
              )}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      <SplitTaskDialog
        open={splitDialogOpen}
        onClose={() => { setSplitDialogOpen(false); setSplitTarget(null); }}
        schedule={splitTarget}
        onConfirm={handleConfirmSplit}
      />

      <DailySettleDialog
        open={settleDialogOpen}
        onClose={() => setSettleDialogOpen(false)}
        dates={dates}
        schedules={schedules}
        onConfirm={handleConfirmSettle}
        initialDate={settleInitialDate}
        singleDay={settleSingleDay}
      />

      <BatchAssignDialog
        open={batchAssignOpen}
        onClose={() => { setBatchAssignOpen(false); setBatchAssignOrder(null); }}
        order={batchAssignOrder}
        workCenters={workCenters}
        onConfirm={handleConfirmBatchAssign}
      />

      <EditScheduleDialog
        open={noteDialogOpen}
        onClose={() => { setNoteDialogOpen(false); setNoteTarget(null); }}
        schedule={noteTarget}
        onConfirm={handleConfirmEdit}
      />

      <QuickAddScheduleDialog
        open={quickAddOpen}
        onClose={() => { setQuickAddOpen(false); setQuickAddTarget(null); }}
        workCenter={quickAddTarget?.workCenter}
        date={quickAddTarget?.date ? format(quickAddTarget.date, 'yyyy-MM-dd') : ''}
        onConfirm={handleConfirmQuickAdd}
      />

      <ScheduleExportImage
        ref={exportContainerRef}
        title={getExportTitle(startDate, endDate)}
        schedules={schedules}
        dates={dates}
        workCenters={workCenters}
        settledDateMap={settledDateMap}
      />
    </Layout>
  );
}

export default ProductionScheduleBoard;
